/**
 * MFE Loader Component
 * 
 * Loads and renders a Micro Frontend (MFE) dynamically.
 * Handles loading states, errors, and prop passing.
 * 
 * @module components/mfe/MFELoader
 * 
 * @example
 * <MFELoader
 *   mfeName="Rewards"
 *   accessToken={token}
 *   refUserId={userId}
 *   onLogout={handleLogout}
 * />
 */

import React, { useState, useEffect, useRef, useCallback, useMemo, Component } from 'react';
import { Provider } from 'react-redux';
import { MfeLoader } from '@/services/mfe/MfeLoader';
import { LoreLoader, Button } from '@sequelae/lore-core';
import { createStreaksStore } from '@/services/mfe/streaksStore';
import './MFELoader.css';

/** Catches cross-origin "Script error" when MFE runs so we show Retry instead of React overlay. */
class MfeRenderErrorBoundary extends Component<
  { children: React.ReactNode; mfeName: string; onRetry: () => void },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError(error: unknown): { hasError: boolean } | null {
    const msg = error instanceof Error ? error.message : String(error);
    if (
      msg === 'Script error.' ||
      msg.includes('Script error') ||
      msg.includes('cross-origin') ||
      msg.includes('useLocation()') ||
      msg.includes('context of a <Router>')
    ) {
      return { hasError: true };
    }
    return null;
  }

  componentDidCatch(error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    if (msg === 'Script error.' || msg.includes('cross-origin')) {
      console.warn('[MFELoader] Caught cross-origin Script error in MFE render; showing Retry.');
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="mfe-error-container">
          <div className="mfe-error-content">
            <h2>Refresh failed</h2>
            <p className="mfe-error-message">The panel could not reload. Try again.</p>
            <Button variant="primary" size="medium" onClick={this.props.onRetry}>
              Retry
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

interface MFELoaderProps {
  mfeName: string;
  accessToken: string;
  refUserId: string;
  onLogout: () => void;
  onError?: (error: Error) => void;
  initialPath?: string; // For UserPreferences MFE to navigate to specific sections
  /** When set (e.g. embedded in home panel), error UI shows "Try Full View" instead of "Go Back" */
  onExpandToFullView?: () => void;
  /** When > 0, cache for this MFE is cleared before load (for panel Refresh). Don't clear on initial load (0). */
  reloadTrigger?: number;
}

export const MFELoader: React.FC<MFELoaderProps> = ({
  mfeName,
  accessToken,
  refUserId,
  onLogout,
  onError,
  initialPath,
  onExpandToFullView,
  reloadTrigger,
}) => {
  const [MfeComponent, setMfeComponent] = useState<React.ComponentType<any> | null>(null);
  const [mfeProps, setMfeProps] = useState<any>(null);
  const [mfeStore, setMfeStore] = useState<any>(null); // State to hold the MFE's Redux store
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  
  // Ref to the MFE container - used by LoreBot to scope its modals/popovers
  const mfeContainerRef = useRef<HTMLDivElement>(null);
  
  // Create Redux store for FAQ/Streaks MFEs (memoized to avoid recreating)
  const streaksStore = useMemo(() => {
    if (mfeName === 'FAQ' || mfeName === 'Streaks') {
      return createStreaksStore();
    }
    return null;
  }, [mfeName]);
  
  // Use refs to avoid stale closures and prevent unnecessary re-runs
  const onLogoutRef = useRef(onLogout);
  const onErrorRef = useRef(onError);
  
  // Update refs when callbacks change
  useEffect(() => {
    onLogoutRef.current = onLogout;
    onErrorRef.current = onError;
  }, [onLogout, onError]);

  // Memoize the logout handler to prevent it from changing
  const stableOnLogout = useCallback(() => {
    onLogoutRef.current();
  }, []);

  // Retry handler: full reset (containers + cache) so next load fetches fresh remoteEntry.js
  const handleRetry = useCallback(() => {
    setIsRetrying(true);
    MfeLoader.clearCacheAndContainers();
    setRetryCount(prev => prev + 1);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadMfe = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Only clear this MFE's cache when refreshing (reloadTrigger > 0). Never clear on initial load.
        if (reloadTrigger != null && reloadTrigger > 0) {
          MfeLoader.clearCacheAndContainersFor(mfeName);
        }

        const { Component, props, store } = await MfeLoader.loadMfe(mfeName, {
          accessToken,
          refUserId,
          onLogout: stableOnLogout,
        });

        // Add initialPath if provided (for UserPreferences MFE)
        // Pass both as direct prop and in dataPackage for compatibility
        if (initialPath && props) {
          // Pass as direct prop (some MFEs expect this)
          props.initialPath = initialPath;
          // Also pass without leading slash (some MFEs expect this format)
          props.initialPathNoSlash = initialPath.startsWith('/') ? initialPath.slice(1) : initialPath;
          // Also pass in dataPackage (iOS format)
          props.dataPackage = {
            initialPath: initialPath,
            initialPathNoSlash: initialPath.startsWith('/') ? initialPath.slice(1) : initialPath
          };
          // Some MFEs might expect it as a route prop
          props.route = initialPath;
          props.path = initialPath;
        }

        if (isMounted) {
          setMfeComponent(() => Component);
          setMfeProps(props);
          setMfeStore(store); // Set the MFE's store if available
          setIsRetrying(false);
          setTimeout(() => setIsLoading(false), 0);
        }
      } catch (err) {
        const loadError = err instanceof Error ? err : new Error('Failed to load MFE');
        if (isMounted) {
          setError(loadError);
          setIsLoading(false);
          setIsRetrying(false);
          onErrorRef.current?.(loadError);
        }
      }
    };

    loadMfe();

    return () => {
      isMounted = false;
    };
  }, [mfeName, accessToken, refUserId, stableOnLogout, retryCount, initialPath, reloadTrigger]);

  // Loading state
  if (isLoading) {
    return (
      <div className="mfe-loader-container">
        <LoreLoader size={75} aria-label={`Loading ${mfeName}...`} />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="mfe-error-container">
        <div className="mfe-error-content">
          <h2>Failed to Load {mfeName}</h2>
          <p className="mfe-error-message">{error.message}</p>
          <div className="mfe-error-actions">
            <Button
              variant="primary"
              size="medium"
              onClick={handleRetry}
              disabled={isRetrying}
            >
              {isRetrying ? 'Retrying…' : 'Retry'}
            </Button>
            <Button
              variant="secondary"
              size="medium"
              onClick={onExpandToFullView ?? (() => window.history.back())}
            >
              {onExpandToFullView ? 'Try Full View' : 'Go Back'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Render MFE
  if (!MfeComponent || !mfeProps) {
    return (
      <div className="mfe-error-container">
        <p>No component available</p>
      </div>
    );
  }

  // Add special classes for specific MFEs
  const getWrapperClass = () => {
    if (mfeName === 'Community') return 'mfe-wrapper mfe-community';
    if (mfeName === 'LoreBot') return 'mfe-wrapper mfe-lorebot';
    if (mfeName === 'UserPreferences') return 'mfe-wrapper mfe-userpreferences';
    if (mfeName === 'Rewards') return 'mfe-wrapper mfe-rewards';
    if (mfeName === 'Verification') return 'mfe-wrapper mfe-verification';
    return 'mfe-wrapper';
  };

  const renderMfe = () => {
    if (mfeName === 'Community') {
      return (
        <div className="mfe-inner-container">
          <MfeComponent {...mfeProps} />
        </div>
      );
    } else if (mfeName === 'LoreBot') {
      // LoreBot needs getWrapper to scope its modals/popovers to this container
      return (
        <MfeComponent
          {...mfeProps}
          getWrapper={() => mfeContainerRef.current}
        />
      );
    } else if (mfeName === 'FAQ' || mfeName === 'Streaks') {
      // FAQ and Streaks need Redux Provider (they use Redux hooks)
      const storeToUse = mfeStore || streaksStore;
      if (storeToUse) {
        return (
          <Provider store={storeToUse}>
            <MfeComponent {...mfeProps} />
          </Provider>
        );
      } else {
        return <MfeComponent {...mfeProps} />;
      }
    } else if (mfeName === 'UserPreferences') {
      // UserPreferences: wrapper for re-render fallback and dialog handling
        const UserPreferencesWrapper = () => {
          const componentRef = useRef<HTMLDivElement>(null);
          const [renderKey, setRenderKey] = useState(0);
          
          useEffect(() => {
            // Check if the MFE component rendered anything; retry once if empty
            const checkTimeout = setTimeout(() => {
              if (componentRef.current) {
                const textContent = componentRef.current.textContent || '';
                const hasContent = componentRef.current.children.length > 0 || textContent.trim().length > 0;
                if (!hasContent && renderKey === 0) {
                  setRenderKey(1);
                }
              }
            }, 1000);
            return () => clearTimeout(checkTimeout);
          }, [renderKey]);

          // Fix confirm button and dialog interactions
          useEffect(() => {
            if (!componentRef.current) return;

            // Store the selected radio value when it changes (event-based)
            const handleRadioChange = (event: Event) => {
              const customEvent = event as CustomEvent;
              const value = customEvent.detail?.value || (event.target as HTMLInputElement)?.value;
              if (value) (window as any).__userPreferencesSelectedValue = value;
            };
            
            document.addEventListener('ionChange', handleRadioChange);
            document.addEventListener('change', handleRadioChange);

            // Move dialogs to body level to avoid sidebar z-index issues
            const moveDialogsToBody = () => {
              componentRef.current?.querySelectorAll('[role="dialog"], [class*="Dialog"], [class*="Modal"], .MuiDialog-root, .MuiModal-root')
                .forEach((dialog) => {
                  if (dialog.parentElement !== document.body) {
                    document.body.appendChild(dialog);
                  }
                });
            };

            // Consolidated API call function
            const saveConsent = async (granted: boolean): Promise<boolean> => {
              try {
                // Check for both 'production' and 'prod' (build script uses 'prod')
                const env = process.env.REACT_APP_ENV || 'development';
                const isProd = env === 'production' || env === 'prod';
                const apiUrl = isProd
                  ? 'https://preferences.lore.co/api/v1'
                  : 'https://preferences-nonprod.lorehealthcare.io/api/v1';
                
                const response = await fetch(
                  `${apiUrl}/privacy/consent/lorebot/ref_user_id/${refUserId}`,
                  {
                    method: 'PUT',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${accessToken}`
                    },
                    body: JSON.stringify({ granted })
                  }
                );
                return response.ok || response.status === 204;
              } catch (error) {
                console.error('[UserPreferences] Error saving consent:', error);
                return false;
              }
            };

            // Helper to hide dialog and backdrop
            const hideDialog = (dialogElement?: Element) => {
              const dialogs = dialogElement 
                ? [dialogElement, ...document.querySelectorAll('[role="dialog"], [class*="Dialog"], [class*="Modal"], .MuiDialog-root, .MuiModal-root')]
                : document.querySelectorAll('[role="dialog"], [class*="Dialog"], [class*="Modal"], .MuiDialog-root, .MuiModal-root');
              
              dialogs.forEach(d => {
                (d as HTMLElement).style.display = 'none';
                d.remove();
              });
              
              document.querySelectorAll('[class*="Backdrop"], .MuiBackdrop-root').forEach(b => {
                (b as HTMLElement).style.display = 'none';
                b.remove();
              });
            };

            // Note: Navigation blocking during consent save is handled by the MFE itself
            // CommunicationBridge handles navigation fallbacks automatically

            // Handle button clicks in consent dialog
            const handleButtonClick = async (e: MouseEvent) => {
              const button = (e.target as HTMLElement).closest('button') as HTMLButtonElement;
              if (!button) return;
              
              const dialog = button.closest('[role="dialog"], [class*="Dialog"], [class*="Modal"], [class*="lorebot-consent"], [class*="consent-modal"]') ||
                           (button.className?.includes('lorebot-consent') || button.textContent?.toLowerCase().includes('confirm') 
                            ? button.parentElement?.parentElement : null);
              if (!dialog) return;
              
              const btnText = button.textContent?.trim().toLowerCase() || '';
              const ariaLabel = button.getAttribute('aria-label')?.toLowerCase() || '';
              const isClose = btnText === '×' || btnText === '✕' || ariaLabel.includes('close') || button.querySelector('svg');
              const isConfirm = btnText.includes('confirm');
              if (!isClose && !isConfirm) return;
              
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              
              const selectedValue = (window as any).__userPreferencesSelectedValue;
              const success = await saveConsent(isConfirm && selectedValue === 'consent');
              
              hideDialog(dialog);
              if (success) setTimeout(() => window.location.href = '/privacy', 300);
            };

            // Use MutationObserver to watch for dialog appearance (only move to body, don't attach handlers)
            const observer = new MutationObserver(() => {
              moveDialogsToBody();
            });

            // Observe the component and body for changes
            if (componentRef.current) {
              observer.observe(componentRef.current, {
                childList: true,
                subtree: true
              });
            }
            observer.observe(document.body, {
              childList: true,
              subtree: true
            });

            document.addEventListener('click', handleButtonClick, true);
            
            return () => {
              observer.disconnect();
              document.removeEventListener('click', handleButtonClick, true);
              document.removeEventListener('ionChange', handleRadioChange);
              document.removeEventListener('change', handleRadioChange);
              delete (window as any).__userPreferencesSelectedValue;
            };
          }, [renderKey]);
        
          return (
            <div 
              ref={componentRef}
              key={`userprefs-wrapper-${renderKey}`}
              style={{ 
                width: '100%', 
                height: '100%', 
                background: 'white', 
                position: 'relative',
                minHeight: '400px', // Ensure minimum height
                display: 'flex',
                flexDirection: 'column'
              }}
              data-initial-path={mfeProps?.initialPath}
              data-render-key={renderKey}
            >
              <MfeComponent {...mfeProps} key={`userprefs-component-${renderKey}`} />
            </div>
          );
        };
        
        return <UserPreferencesWrapper />;
    }
    return <MfeComponent {...mfeProps} />;
  };

  return (
    <div
      ref={mfeContainerRef}
      className={getWrapperClass()}
      data-mfe={mfeName}
    >
      <MfeRenderErrorBoundary
        key={`mfe-render-boundary-${retryCount}`}
        mfeName={mfeName}
        onRetry={handleRetry}
      >
        {renderMfe()}
      </MfeRenderErrorBoundary>
    </div>
  );
};

